import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Form, Button, Alert, Spinner } from 'react-bootstrap'
import api from '../services/api'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [validating, setValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [username, setUsername] = useState('')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!token) {
      setValidating(false)
      return
    }

    validateToken()
  }, [token])

  const validateToken = async () => {
    try {
      const res = await api.get(`/auth/reset-password/validate?token=${token}`)
      if (res.data.valid) {
        setTokenValid(true)
        setUsername(res.data.username)
      } else {
        setError(res.data.message || 'Ungültiger Token')
      }
    } catch (err) {
      setError('Token konnte nicht validiert werden')
    } finally {
      setValidating(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein')
      return
    }

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein')
      return
    }

    setLoading(true)

    try {
      await api.post('/auth/reset-password', {
        token: token,
        new_password: password
      })
      setSuccess(true)
      setTimeout(() => navigate('/login'), 3000)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Ein Fehler ist aufgetreten')
    } finally {
      setLoading(false)
    }
  }

  // Loading state
  if (validating) {
    return (
      <div className="login-container">
        <div className="login-card text-center">
          <Spinner animation="border" className="mb-3" />
          <p className="text-muted">Token wird überprüft...</p>
        </div>
      </div>
    )
  }

  // No token provided
  if (!token) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <i className="bi bi-exclamation-triangle text-warning"></i>
            <h2 className="mt-3">Kein Token</h2>
          </div>

          <Alert variant="warning">
            Kein Reset-Token angegeben. Bitte nutzen Sie den Link aus der E-Mail.
          </Alert>

          <Link to="/forgot-password" className="btn btn-primary w-100 mb-3">
            <i className="bi bi-envelope me-2"></i>
            Neuen Link anfordern
          </Link>

          <Link to="/login" className="btn btn-outline-secondary w-100">
            <i className="bi bi-arrow-left me-2"></i>
            Zur Anmeldung
          </Link>
        </div>
      </div>
    )
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <i className="bi bi-x-circle text-danger"></i>
            <h2 className="mt-3">Ungültiger Link</h2>
          </div>

          <Alert variant="danger">
            {error || 'Der Reset-Link ist ungültig oder abgelaufen.'}
          </Alert>

          <Link to="/forgot-password" className="btn btn-primary w-100 mb-3">
            <i className="bi bi-envelope me-2"></i>
            Neuen Link anfordern
          </Link>

          <Link to="/login" className="btn btn-outline-secondary w-100">
            <i className="bi bi-arrow-left me-2"></i>
            Zur Anmeldung
          </Link>
        </div>
      </div>
    )
  }

  // Success state
  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <i className="bi bi-check-circle text-success"></i>
            <h2 className="mt-3">Passwort geändert</h2>
          </div>

          <Alert variant="success">
            Ihr Passwort wurde erfolgreich zurückgesetzt.
          </Alert>

          <p className="text-muted text-center mb-4">
            Sie werden in Kürze zur Anmeldung weitergeleitet...
          </p>

          <Link to="/login" className="btn btn-primary w-100">
            <i className="bi bi-box-arrow-in-right me-2"></i>
            Jetzt anmelden
          </Link>
        </div>
      </div>
    )
  }

  // Reset form
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <i className="bi bi-key"></i>
          <h2 className="mt-3">Neues Passwort</h2>
          <p className="text-muted">Hallo {username}</p>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Neues Passwort</Form.Label>
            <div className="input-group">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                required
                autoFocus
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </Button>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Passwort bestätigen</Form.Label>
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
              required
              isInvalid={confirmPassword.length > 0 && password !== confirmPassword}
            />
            <Form.Control.Feedback type="invalid">
              Passwörter stimmen nicht überein
            </Form.Control.Feedback>
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            className="w-100 mb-3"
            disabled={loading || !password || password !== confirmPassword}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Wird gespeichert...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Passwort speichern
              </>
            )}
          </Button>

          <Link to="/login" className="btn btn-outline-secondary w-100">
            <i className="bi bi-arrow-left me-2"></i>
            Abbrechen
          </Link>
        </Form>
      </div>
    </div>
  )
}
